/* Subscription purchase options on eligible recommendation cards, BEFORE add.
 * One-time purchases continue through AfterSell's original Add handler. */
(() => {
  const node = document.getElementById('aftersell-cart-subscription-data');
  const products = new Map();
  const requests = new Map();
  if (node) {
    const initial = JSON.parse(node.textContent);
    products.set(initial.handle, initial);
  }
  const states = new WeakMap();
  let sequence = 0;

  const create = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  };

  function variantFor(card, product) {
    const picker = card.querySelector('.cart-external-upsells__variant');
    const id = picker?.value || Object.keys(product.variants).find(key => product.variants[key].available);
    return { id, variant: product.variants[id] };
  }

  function handleFor(card) {
    const link = card.querySelector('a[href]');
    if (!link) return null;
    try {
      const path = new URL(link.href, location.href).pathname.replace(/\/$/, '');
      return path.match(/\/products\/([^/]+)$/)?.[1] || null;
    } catch { return null; }
  }

  async function loadProduct(handle, api) {
    if (products.has(handle)) return products.get(handle);
    if (requests.has(handle)) return requests.get(handle);
    const request = (async () => {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Purchase options are unavailable. Please try again.');
      const raw = await response.json();
      const plans = new Map();
      for (const group of raw.selling_plan_groups || []) {
        for (const plan of group.selling_plans || []) plans.set(String(plan.id), plan);
      }
      const product = { handle, variants: {} };
      for (const variant of raw.variants || []) {
        product.variants[String(variant.id)] = {
          available: variant.available,
          quantity: variant.quantity_rule?.min || 1,
          requiresPlan: variant.requires_selling_plan ?? raw.requires_selling_plan ?? false,
          plans: (variant.selling_plan_allocations || []).flatMap(allocation => {
            const plan = plans.get(String(allocation.selling_plan_id));
            if (!plan) return [];
            return [{
              id: String(plan.id), name: plan.name,
              price: api.actions.formatMoney(allocation.per_delivery_price ?? allocation.price)
            }];
          })
        };
      }
      products.set(handle, product);
      return product;
    })();
    requests.set(handle, request);
    try { return await request; }
    finally { requests.delete(handle); }
  }

  function buildOptions(card, state) {
    const { id, variant } = variantFor(card, state.product);
    state.variantId = id;
    state.select.replaceChildren();
    state.controls.hidden = !variant?.plans.length;
    if (!variant) return;
    const option = (value, text) => {
      const item = create('option', '', text);
      item.value = value;
      state.select.append(item);
    };
    for (const plan of variant.plans) option(plan.id, plan.name.replace(/^Delivery:\s*/i, ''));
    if (!variant.requiresPlan) option('', 'One-time purchase');
    // Default to a real plan, while retaining an explicit one-time choice when
    // the same card/variant is rerendered by the app.
    const remembered = state.choices.get(String(id));
    const validChoice = remembered === '' ? !variant.requiresPlan
      : variant.plans.some(plan => plan.id === remembered);
    state.select.value = validChoice ? remembered : (variant.plans[0]?.id || '');
    updateChoice(card, state);
  }

  function updateChoice(card, state) {
    const variant = state.product.variants[state.variantId];
    const plan = variant?.plans.find(item => item.id === state.select.value);
    state.details.textContent = plan ? (plan.description || 'Recurring delivery. Billed automatically on your selected schedule.') : '';
    state.details.hidden = !state.details.textContent;
    state.price.textContent = plan ? `${plan.price} / delivery` : '';
    state.price.hidden = !plan;
    card.toggleAttribute('data-theme-subscription-selected', Boolean(plan));
    state.status.textContent = '';
  }

  async function addSubscription(card, state, api) {
    if (state.pending) return;
    const { id, variant } = variantFor(card, state.product);
    const plan = variant?.plans.find(item => item.id === state.select.value);
    if (!variant?.available || !plan) return;
    state.pending = true;
    const button = card.querySelector('.cart-external-upsells__add');
    const picker = card.querySelector('.cart-external-upsells__variant');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    state.select.disabled = true;
    if (picker) picker.disabled = true;
    state.status.textContent = 'Adding subscription…';
    let added = false, refreshed = false;
    try {
      const write = async () => {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: Number(id), quantity: variant.quantity, selling_plan: Number(plan.id) })
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(typeof result.description === 'string' ? result.description : 'Unable to add this subscription. Please try again.');
        }
        // Do not retry an accepted add if only the cart refresh fails.
        added = true;
      };
      if (navigator.locks?.request) await navigator.locks.request('aftersell-cart-write', write);
      else await write();
      await api.actions.refresh();
      refreshed = true;
      await api.actions.open();
      state.status.textContent = 'Subscription added to cart.';
    } catch (error) {
      state.status.textContent = added
        ? 'Subscription added. Refresh the page to update your cart.'
        : error.message;
    } finally {
      // An uncertain refresh must not turn the same click into a second add.
      state.pending = false;
      button.disabled = added && !refreshed;
      button.removeAttribute('aria-busy');
      state.select.disabled = added && !refreshed;
      if (picker) picker.disabled = added && !refreshed;
      state.added = added && !refreshed;
    }
  }

  function connect(api, shadow) {
    const style = create('style');
    style.textContent = `
      .theme-upsell-subscription{width:100%;min-width:0;box-sizing:border-box;margin:8px 0}
      .theme-upsell-subscription label{display:block;font-size:12px;line-height:1.4;margin-bottom:4px}
      .theme-upsell-subscription select{display:block;width:100%;min-width:0;box-sizing:border-box;min-height:40px;border:1px solid #bbb;border-radius:4px;background:var(--aftersell-cart-background,#fff);color:var(--aftersell-cart-text,#222);padding:6px;font:inherit;font-size:14px}
      .theme-upsell-subscription p{font-size:12px;line-height:1.4;margin:6px 0 0}
      .theme-upsell-subscription__price{font-weight:600;font-size:15px!important}
      .theme-upsell-subscription [hidden]{display:none!important}
      .theme-upsell-subscription :focus-visible{outline:2px solid currentColor;outline-offset:2px}
      .theme-upsell-subscription [role=status]:empty{display:none}
      .cart-external-upsells__card[data-theme-subscription-card] .cart-external-upsells__actions{flex-wrap:wrap}
      .cart-external-upsells__card[data-theme-subscription-selected] .cart-external-upsells__prices{display:none}
    `;
    shadow.append(style);
    const scan = () => {
      for (const card of shadow.querySelectorAll('.cart-external-upsells__card')) {
        const handle = handleFor(card);
        if (!handle) continue;
        const actions = card.querySelector('.cart-external-upsells__actions');
        const button = card.querySelector('.cart-external-upsells__add');
        if (!actions || !button) continue;
        let state = states.get(card);
        if (state && state.handle !== handle) {
          state.controls.remove();
          card.removeAttribute('data-theme-subscription-selected');
          card.removeAttribute('data-theme-subscription-card');
          states.delete(card);
          state = null;
        }
        if (!state) {
          const controls = create('div', 'theme-upsell-subscription');
          const label = create('label', '', 'Purchase option');
          const select = create('select');
          select.id = `upsell-purchase-option-${++sequence}`;
          label.htmlFor = select.id;
          const price = create('p', 'theme-upsell-subscription__price');
          const details = create('p');
          const status = create('p');
          status.setAttribute('role', 'status');
          controls.append(label, select, price, details, status);
          state = { controls, select, price, details, status, handle, choices: new Map(), loading: true };
          states.set(card, state);
          controls.hidden = true;
          select.addEventListener('change', () => {
            state.choices.set(String(state.variantId), select.value);
            updateChoice(card, state);
          });
          card.addEventListener('change', event => {
            if (states.get(card) === state && state.product
              && event.target.matches('.cart-external-upsells__variant')) buildOptions(card, state);
          });
          const load = () => loadProduct(handle, api).then(product => {
            if (states.get(card) !== state) return;
            state.product = product;
            state.loading = false;
            state.failed = false;
            card.setAttribute('data-theme-subscription-card', '');
            buildOptions(card, state);
          }).catch(() => {
            if (states.get(card) !== state) return;
            state.loading = false;
            state.failed = true;
            select.hidden = true;
            label.hidden = true;
            controls.hidden = false;
            status.textContent = 'Purchase options could not load. Click Add to retry.';
          });
          state.retry = () => { state.loading = true; load(); };
          load();
        } else if (state.product && variantFor(card, state.product).id !== state.variantId) {
          buildOptions(card, state);
        }
        if (state.controls.parentNode !== actions) actions.insertBefore(state.controls, button);
      }
    };
    // Catch the existing Add button before the app's bubbling click handler.
    // Never silently add as one-time while a product's plans are still loading.
    shadow.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('.cart-external-upsells__add');
      const card = button?.closest('.cart-external-upsells__card');
      const state = card && states.get(card);
      if (!state) return;
      if (state.loading || state.failed) {
        event.preventDefault();
        event.stopImmediatePropagation();
        state.controls.hidden = false;
        state.status.textContent = 'Loading purchase options. Please try Add again when ready.';
        if (state.failed) {
          state.select.hidden = false;
          state.controls.querySelector('label').hidden = false;
          state.retry();
        }
        return;
      }
      if (!state.select.value) return;
      if (button.disabled && !state.pending && !state.added) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!state.pending && !state.added) addSubscription(card, state, api);
    }, true);
    new MutationObserver(scan).observe(shadow, { childList: true, subtree: true });
    scan();
  }

  let attempts = 0, connecting = false;
  const poll = setInterval(async () => {
    if (connecting) return;
    const root = document.getElementById('aftersell-cart-root');
    const api = window.aftersell?.cart;
    if (++attempts > 300) { clearInterval(poll); return; }
    if (!root?.shadowRoot || !api?.actions?.refresh || !api?.actions?.open
      || !api?.actions?.formatMoney) return;
    connecting = true;
    try {
      if (typeof api.ready === 'function') await api.ready();
      connect(api, root.shadowRoot);
      clearInterval(poll);
    } catch (error) {
      console.warn('Upsell subscription controls unavailable', error);
    } finally { connecting = false; }
  }, 100);
})();
